import React,{useState,useMemo,useEffect} from 'react';
import RecipeCard from '../../components/recipes/RecipeCard';
import SafeIcon from '../../common/SafeIcon';
import Button from '../../components/ui/Button';

const getProp=(obj,keys)=> {
  if (!obj) return "";
  const lowerKeys=keys.map(k=> k.toLowerCase());
  for (const key of Object.keys(obj)) {
    if (lowerKeys.includes(key.toLowerCase())) return obj[key];
  }
  return "";
};

const transformRecipeData=(data)=> {
  if (!data) return data;
  const rename=(str)=> typeof str==='string' ? str.replace(/UBEA Ube Extract/g,'UBEA Ube Essence').replace(/Ube Extract/g,'Ube Essence').replace(/ube extract/g,'ube essence') : str;
  const processObject=(obj)=> {
    const newObj={...obj};
    for (const key in newObj) {
      if (typeof newObj[key]==='string') newObj[key]=rename(newObj[key]);
      else if (Array.isArray(newObj[key])) newObj[key]=newObj[key].map(item=> typeof item==='string' ? rename(item) : (typeof item==='object' ? processObject(item) : item));
      else if (newObj[key] !==null && typeof newObj[key]==='object') newObj[key]=processObject(newObj[key]);
    }
    return newObj;
  };
  if (Array.isArray(data)) return data.map(recipe=> processObject(recipe));
  if (data.recipes && Array.isArray(data.recipes)) return {...data,recipes: data.recipes.map(recipe=> processObject(recipe))};
  return data;
};

const Recipes=()=> {
  const [selectedCuisines,setSelectedCuisines]=useState([]);
  const [selectedProducts,setSelectedProducts]=useState([]);
  const [selectedCategories,setSelectedCategories]=useState([]);
  const [selectedTags,setSelectedTags]=useState([]);
  const [liveData,setLiveData]=useState(null);

  useEffect(()=> {
    fetch('https://media-manager-c.questera.ai/greta-media/2f6847c5255962cb7ef6c818c843620d0179d6b0e810fa4ca1144f00d96707af10e5b2228b1b15861dc89ecefec6c49b/documents/YXBwbGljYXRpb24vanNvbg==/c511ac5c7aacfcfc68e1ca02573fc165.json')
      .then(res=> res.json())
      .then(data=> {
        const transformed=transformRecipeData(data);
        setLiveData(Array.isArray(transformed) ? transformed : (transformed.recipes || []));
      })
      .catch(err=> {
        console.error("Error fetching recipes:",err);
        setLiveData([]);
      });
  },[]);

  const filters={
    cuisines: ['Filipino Classics','American-Inspired','European-Inspired','Asian-Inspired','Middle Eastern-Inspired','Latin-Inspired','Fusion'],
    products: ['Ube Powder','Ube Essence','Ube Syrup','Ube Syrup Sugar-Free (Monk Fruit)','Ube Protein Powder'],
    categories: ['Drinks','Breakfast','Breads','Cakes & Desserts','Cookies & Bars','Pastry Shop & Fusion','Frozen & Classics','Protein Kitchen','Ube + Cheese','Trending & Elevated','Sugar-Free (Monk Fruit)'],
    tags: ['Sugar-Free','Sugar-Free Option','Keto-Friendly','Wellness','High-Protein','Trending','Ube + Cheese','Cafe Menu','Quick & Easy','No-Bake']
  };

  const toggleFilter=(group,value)=> {
    if (group==='products') {
      setSelectedProducts(value==='All' ? [] : [value]);
      return;
    }
    const current={cuisines: selectedCuisines,categories: selectedCategories,tags: selectedTags}[group];
    const setter={cuisines: setSelectedCuisines,categories: setSelectedCategories,tags: setSelectedTags}[group];
    if (value==='All') {
      setter([]);
      return;
    }
    setter(current.includes(value) ? current.filter(v=> v !==value) : [...current,value]);
  };

  const filteredRecipes=useMemo(()=> {
    if (!liveData) return [];
    return liveData.filter(recipe=> {
      const rCuisine=String(getProp(recipe,['cuisine']) || "").trim().toLowerCase();
      const matchCuisine=selectedCuisines.length===0 || selectedCuisines.some(c=> c.toLowerCase()===rCuisine);
      const productMatches=[];
      const madeWith=getProp(recipe,['made_with','collection','product_filters']);
      if (Array.isArray(madeWith)) productMatches.push(...madeWith.map(m=> String(m).toLowerCase()));
      else if (madeWith) productMatches.push(String(madeWith).toLowerCase());
      const ingredients=getProp(recipe,['ingredients','ingredient']);
      if (Array.isArray(ingredients)) {
        ingredients.forEach(ing=> {
          const lowerIng=String(ing).toLowerCase();
          if (lowerIng.includes('powder')) productMatches.push('ube powder');
          if (lowerIng.includes('essence')) productMatches.push('ube essence');
          if (lowerIng.includes('syrup')) productMatches.push(lowerIng.includes('sugar free') || lowerIng.includes('monk fruit') ? 'ube syrup sugar-free (monk fruit)' : 'ube syrup');
          if (lowerIng.includes('protein')) productMatches.push('ube protein powder');
        });
      }
      const matchProduct=selectedProducts.length===0 || selectedProducts.some(selectedP=> {
        const p=selectedP.toLowerCase();
        return productMatches.some(m=> m.includes(p) || p.includes(m));
      });
      const rCategory=String(getProp(recipe,['category']) || "").trim().toLowerCase();
      const matchCategory=selectedCategories.length===0 || selectedCategories.some(cat=> cat.toLowerCase()===rCategory);
      const rTags=getProp(recipe,['tags','tag']);
      const recipeTags=Array.isArray(rTags) ? rTags.map(t=> String(t).toLowerCase()) : [];
      const matchTag=selectedTags.length===0 || selectedTags.some(t=> recipeTags.includes(t.toLowerCase()));
      return matchCuisine && matchProduct && matchCategory && matchTag;
    });
  },[liveData,selectedCuisines,selectedProducts,selectedCategories,selectedTags]);

  const resetAll=()=> {
    setSelectedCuisines([]);
    setSelectedProducts([]);
    setSelectedCategories([]);
    setSelectedTags([]);
  };
  const isFiltering=selectedCuisines.length>0 || selectedProducts.length>0 || selectedCategories.length>0 || selectedTags.length>0;

  return (
    <div className="bg-white min-h-screen pt-12 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-serif text-4xl md:text-5xl text-gray-900 mb-4 tracking-tight">Recipe Library</h1>
          <p className="text-gray-600 max-w-2xl mx-auto text-balance">From our test kitchen to yours — every recipe developed with real Philippine ube.</p>
        </div>
        <div className="bg-sand-light rounded-3xl p-6 md:p-8 mb-16 shadow-sm border border-sand">
          <div className="mb-8">
            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4">By Cuisine</h3>
            <div className="flex flex-wrap gap-2">
              <button onClick={()=> toggleFilter('cuisines','All')} className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${selectedCuisines.length===0 ? 'bg-ube text-white' : 'bg-white text-gray-600 border border-gray-100'}`}>All Cuisines</button>
              {filters.cuisines.map(c=> <button key={c} onClick={()=> toggleFilter('cuisines',c)} className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${selectedCuisines.includes(c) ? 'bg-ube text-white shadow-md' : 'bg-white text-gray-600 border border-gray-100'}`}>{c}</button>)}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4">By Product</h3>
              <div className="flex flex-wrap gap-2">
                <button onClick={()=> toggleFilter('products','All')} className={`px-4 py-2 rounded-lg text-xs transition-all border ${selectedProducts.length===0 ? 'bg-ube text-white font-bold border-ube shadow-sm' : 'bg-white text-gray-500 border-gray-100 hover:border-sand'}`}>All Products</button>
                {filters.products.map(p=> <button key={p} onClick={()=> toggleFilter('products',p)} className={`px-4 py-2 rounded-lg text-xs transition-all border ${selectedProducts.includes(p) ? 'bg-ube text-white font-bold border-ube shadow-sm' : 'bg-white text-gray-500 border-gray-100 hover:border-sand'}`}>{p}</button>)}
              </div>
            </div>
            <div>
              <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4">By Lifestyle & Tag</h3>
              <div className="flex flex-wrap gap-2">
                <button onClick={()=> toggleFilter('tags','All')} className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all border ${selectedTags.length===0 ? 'bg-coral text-white border-coral' : 'bg-white text-gray-400 border-gray-100'}`}>All Tags</button>
                {filters.tags.map(t=> <button key={t} onClick={()=> toggleFilter('tags',t)} className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all border ${selectedTags.includes(t) ? 'bg-coral text-white border-coral' : 'bg-white text-gray-400 border-gray-100'}`}>{t}</button>)}
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-sand">
            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-4">By Category</h3>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              <button onClick={()=> toggleFilter('categories','All')} className={`text-xs transition-colors ${selectedCategories.length===0 ? 'text-ube font-bold underline decoration-2 underline-offset-4' : 'text-gray-400 hover:text-ube'}`}>All Categories</button>
              {filters.categories.map(cat=> <button key={cat} onClick={()=> toggleFilter('categories',cat)} className={`text-xs transition-colors ${selectedCategories.includes(cat) ? 'text-ube font-bold underline decoration-2 underline-offset-4' : 'text-gray-400 hover:text-ube'}`}>{cat}</button>)}
            </div>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-lg font-medium text-gray-900">
              {!liveData ? <span className="flex items-center gap-2"><SafeIcon name="Loader" className="w-4 h-4 animate-spin text-ube" />Loading recipes...</span> : isFiltering ? `Showing ${filteredRecipes.length} matches` : `All ${filteredRecipes.length} Recipes`}
            </h2>
            {isFiltering && <button onClick={resetAll} className="text-ube text-sm font-bold flex items-center hover:opacity-70"><SafeIcon name="X" className="w-4 h-4 mr-1" />Clear all filters</button>}
          </div>
          {filteredRecipes.length>0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filteredRecipes.map((recipe,index)=> <RecipeCard key={getProp(recipe,['id','slug']) || index} recipe={recipe} />)}
            </div>
          ) : liveData ? (
            <div className="text-center py-32 bg-sand-light rounded-[3rem] border border-sand">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm"><SafeIcon name="Search" className="w-8 h-8 text-ube/20" /></div>
              <h3 className="font-serif text-2xl text-gray-900 mb-2">{selectedProducts.length>0 ? "No recipes yet for this product" : "No recipes match"}</h3>
              <p className="text-gray-500 mb-8">Try selecting a different filter or checking back soon for new test kitchen releases.</p>
              <Button onClick={resetAll} variant="primary">Clear all filters</Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Recipes;